import { type ReactNode } from 'react';
import type { CollectorSummary } from '../types';
import { formatUSD } from '../utils/payment-formatters';
import { buildRows, type Row } from './accumulated-rows';

const TH = 'pb-1 text-left font-medium';
const TD = 'py-1 text-xs text-text-secondary';
const AMOUNT_CLS =
  'whitespace-nowrap py-1 text-right font-mono text-xs tabular-nums';

function amountClass(variant: Row['variant']): string {
  if (variant === 'penalty') return `${AMOUNT_CLS} text-error`;
  return `${AMOUNT_CLS} text-text-secondary`;
}

function DataRow({ r }: { r: Row }): ReactNode {
  return (
    <tr className="border-t border-white/[0.04]">
      <td className={`${TD} whitespace-nowrap`}>{r.concept}</td>
      <td className="whitespace-nowrap py-1 text-[10px] text-text-tertiary">
        {r.period}
      </td>
      <td className={amountClass(r.variant)}>{formatUSD(r.amount)}</td>
    </tr>
  );
}

function TotalRow({ total }: { total: number }): ReactNode {
  return (
    <tr className="border-t border-white/[0.08]">
      <td className="py-sm text-xs font-semibold text-text-primary" colSpan={2}>
        Total
      </td>
      <td className="py-sm text-right font-mono text-xs font-bold tabular-nums text-text-primary">
        {formatUSD(total)}
      </td>
    </tr>
  );
}

function TableBody({ rows, total }: { rows: Row[]; total: number }): ReactNode {
  return (
    <div className="-mx-base overflow-x-auto px-base">
      <table className="w-full min-w-[22rem] text-[10px] text-text-tertiary">
        <thead>
          <tr>
            <th className={TH}>Concepto</th>
            <th className={TH}>Periodo</th>
            <th className="pb-1 text-right font-medium">Importe</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <DataRow key={i} r={r} />
          ))}
          <TotalRow total={total} />
        </tbody>
      </table>
    </div>
  );
}

type Props = { summary: CollectorSummary; baseEarnings: number };

export function AccumulatedTable({ summary, baseEarnings }: Props): ReactNode {
  const rows = buildRows(summary, baseEarnings);
  const total = rows.reduce((s, r) => s + r.amount, 0);
  return (
    <div className="rounded-xl bg-white/[0.02] p-base">
      <h3 className="mb-sm text-[10px] font-medium uppercase tracking-wider text-text-tertiary/60">
        Acumulado
      </h3>
      <TableBody rows={rows} total={total} />
    </div>
  );
}
